import { useState, useEffect, forwardRef } from "react";
import { PiUsers } from "react-icons/pi";
import { IoCloseOutline } from "react-icons/io5";
import InputText from "../input/InputText";
import toast from "react-hot-toast";
import useTransaction from "../../hooks/useTransaction";
import useResource from "../../hooks/useResource";
import SearchableDropdown2 from "../input/SearchableDropdown2";
import { FieldArray, useFormik, FormikProvider } from "formik";
import { useSelector } from "react-redux";
import {
  createTransactionSchema,
  createTransactionSchemaTwo,
} from "../../utils/validationSchema/transactionSchema";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatNumberOnInput,
  parseNumberInput,
  toMoney,
} from "../../utils/utils";

const matchOption = (list, id, name) =>
  list?.find(
    (item) =>
      item.value === id ||
      item.value === Number(id) ||
      String(item.value) === String(id) ||
      item.label === name,
  ) || null;

export const TransactionModal = ({
  closeModal,
  aggregatorList,
  collectorList,
  requestType,
  fetchTransactions,
  userData,
  isAggregator,
  selectOptDefault,
}) => {
  const [loading, setLoading] = useState(false);
  const { getAllSourceOfPlastics, getAllTypeOfPlastics } = useResource();
  const { createNewTransaction, updateExistingTransaction } = useTransaction();
  const [plasticTypeList, setPlasticTypeList] = useState([]);
  const [plasticSourceList, setPlasticSourceList] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [itemErrors, setItemErrors] = useState({});
  const isEdit = requestType === "edit";

  const [newCollection, setNewCollection] = useState({
    amount:
      isEdit && userData?.amount != null
        ? formatNumberOnInput(String(userData.amount))
        : "",
    quantity:
      isEdit && userData?.quantity != null
        ? formatNumberOnInput(String(userData.quantity), {
            allowDecimals: true,
            maxDecimals: 2,
          })
        : "",
    price: isEdit ? userData?.price ?? 0 : 0,
    collectorId: isEdit ? userData?.collectorId ?? null : null,
    collectorName: isEdit ? userData?.collector ?? "" : "",
    aggregatorId: isEdit ? userData?.aggregatorId ?? null : null,
    aggregatorName: isEdit ? userData?.aggregator ?? "" : "",
    typeOfPlasticId: isEdit ? userData?.typeOfPlasticId ?? null : null,
    typeOfPlasticName: isEdit ? userData?.typeOfPlastic ?? "" : "",
    sourceOfPlasticId: isEdit ? userData?.sourceOfPlasticId ?? null : null,
    sourceOfPlasticName: isEdit ? userData?.sourceOfPlastic ?? "" : "",
    transactionDate: isEdit ? userData?.transactionDate ?? null : null,
  });
  const { program, user } = useSelector((state) => state.auth);

  const updateCollectionField = (updates) => {
    setNewCollection((prev) => ({ ...prev, ...updates }));
    setItemErrors((prev) => {
      const next = { ...prev };
      Object.keys(updates).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const getCollectionErrors = (collection) => {
    const errors = {};
    const quantity = parseNumberInput(collection.quantity);
    const price = parseNumberInput(collection.price);
    const amount = parseNumberInput(collection.amount);

    if (!isAggregator && !collection.aggregatorId) {
      errors.aggregatorId = "Aggregator is required";
    }
    if (!collection.collectorId) {
      errors.collectorId = "Collector is required";
    }
    if (!collection.quantity || quantity <= 0) {
      errors.quantity = "Quantity is required";
    }
    if (program === 1 && (!collection.price || price <= 0)) {
      errors.price = "Price is required";
    }
    if ((program === 2 || program === 3) && (!collection.amount || amount <= 0)) {
      errors.amount = "Amount is required";
    }
    if (!collection.sourceOfPlasticId) {
      errors.sourceOfPlasticId = "Source of plastic is required";
    }
    if (!collection.typeOfPlasticId) {
      errors.typeOfPlasticId = "Type of plastic is required";
    }
    if (!collection.transactionDate) {
      errors.transactionDate = "Transaction date is required";
    }

    return errors;
  };

  useEffect(() => {
    const getPlasticTypes = async () => {
      const res = JSON.parse(await getAllTypeOfPlastics());
      const list = res?.data?.map((item) => {
        return {
          label: item.name,
          value: item.id,
        };
      });

      setPlasticTypeList([...list]);
    };

    const getPlasticSources = async () => {
      const res = JSON.parse(await getAllSourceOfPlastics());
      const list = res?.data?.map((item) => {
        return {
          label: item.name,
          value: item.id,
        };
      });

      setPlasticSourceList([...list]);
    };
    getPlasticTypes();
    getPlasticSources();
  }, []);

  useEffect(() => {
    if (!isEdit || !userData) return;

    const aggregator = matchOption(
      aggregatorList,
      userData.aggregatorId,
      userData.aggregator,
    );
    const collector = matchOption(
      collectorList,
      userData.collectorId,
      userData.collector,
    );
    const typeOfPlastic = matchOption(
      plasticTypeList,
      userData.typeOfPlasticId,
      userData.typeOfPlastic,
    );
    const sourceOfPlastic = matchOption(
      plasticSourceList,
      userData.sourceOfPlasticId,
      userData.sourceOfPlastic,
    );

    setNewCollection((prev) => ({
      ...prev,
      ...(aggregator
        ? { aggregatorId: aggregator.value, aggregatorName: aggregator.label }
        : {}),
      ...(collector
        ? { collectorId: collector.value, collectorName: collector.label }
        : {}),
      ...(typeOfPlastic
        ? {
            typeOfPlasticId: typeOfPlastic.value,
            typeOfPlasticName: typeOfPlastic.label,
          }
        : {}),
      ...(sourceOfPlastic
        ? {
            sourceOfPlasticId: sourceOfPlastic.value,
            sourceOfPlasticName: sourceOfPlastic.label,
          }
        : {}),
    }));
  }, [
    isEdit,
    userData,
    aggregatorList,
    collectorList,
    plasticTypeList,
    plasticSourceList,
  ]);

  const initialValues = {
    plastics: [],
  };

  const onSubmit = async (data) => {
    const newData = data.plastics.map((obj, index) => {
      return {
        amount: obj.amount,
        quantity: obj.quantity,
        price: obj.price,
        programId: program,
        collectorId: obj.collectorId,
        aggregatorId: isAggregator ? selectOptDefault?.value : obj.aggregatorId,
        typeOfPlasticId: obj.typeOfPlasticId,
        sourceOfPlasticId: obj.sourceOfPlasticId,
        transactionDate: obj.transactionDate,
      };
    });
    try {
      setLoading(true);
      const res = await createNewTransaction(newData);
      if (res.code === "00") {
        toast.success("Trasaction created");
        closeModal();
      }
      if (res.code !== "00") {
        setLoading(false);
        toast.error(res.message || "Error creating Transaction");
      }
    } catch (error) {
      toast.error(error.message || "something went wrong");
    } finally {
      setLoading(false);
      fetchTransactions();
    }
  };

  const handleAddItem = () => {
    const errors = getCollectionErrors(newCollection);
    if (Object.keys(errors).length > 0) {
      setItemErrors(errors);
      toast.error("Please complete all required fields");
      return;
    }

    const newPlastics = [
      ...formik.values.plastics,
      {
        amount:
          program === 1
            ? parseNumberInput(newCollection.quantity) *
              parseNumberInput(newCollection.price)
            : parseNumberInput(newCollection.amount),
        quantity: parseNumberInput(newCollection.quantity),
        price: Number(newCollection.price)
          ? Number(newCollection.price)
          : 0,
        collectorId: newCollection.collectorId,
        collectorName: newCollection.collectorName,
        aggregatorId: newCollection.aggregatorId,
        aggregatorName: newCollection.aggregatorName,
        typeOfPlasticId: newCollection.typeOfPlasticId,
        typeOfPlasticName: newCollection.typeOfPlasticName,
        sourceOfPlasticId: newCollection.sourceOfPlasticId,
        sourceOfPlasticName: newCollection.sourceOfPlasticName,
        transactionDate: newCollection.transactionDate,
      },
    ];
    formik.setFieldValue("plastics", newPlastics);
    setItemErrors({});
    setNewCollection({
      amount: "",
      quantity: "",
      price: 0,
      collectorId: newCollection.collectorId,
      collectorName: newCollection.collectorName,
      aggregatorId: newCollection.aggregatorId,
      aggregatorName: newCollection.aggregatorName,
      typeOfPlasticId: newCollection.typeOfPlasticId,
      typeOfPlasticName: newCollection.typeOfPlasticName,
      sourceOfPlasticId: newCollection.sourceOfPlasticId,
      sourceOfPlasticName: newCollection.sourceOfPlasticName,
      transactionDate: newCollection.transactionDate,
    });
  };

  const handleUpdate = async () => {
    const errors = getCollectionErrors(newCollection);
    if (Object.keys(errors).length > 0) {
      setItemErrors(errors);
      toast.error("Please complete all required fields");
      return;
    }

    const payload = {
      id: userData.id,
      amount:
        program === 1
          ? parseNumberInput(newCollection.quantity) *
            parseNumberInput(newCollection.price)
          : parseNumberInput(newCollection.amount),
      quantity: parseNumberInput(newCollection.quantity),
      price: Number(newCollection.price) ? Number(newCollection.price) : 0,
      programId: program,
      collectorId: newCollection.collectorId,
      aggregatorId: isAggregator
        ? selectOptDefault?.value
        : newCollection.aggregatorId,
      typeOfPlasticId: newCollection.typeOfPlasticId,
      sourceOfPlasticId: newCollection.sourceOfPlasticId,
      transactionDate: newCollection.transactionDate,
    };

    try {
      setLoading(true);
      const res = await updateExistingTransaction(payload);
      if (res.code === "00") {
        toast.success("Transaction updated");
        closeModal();
      }
      if (res.code !== "00") {
        setLoading(false);
        toast.error(res.message || "Error updating Transaction");
      }
    } catch (error) {
      toast.error(error.message || "something went wrong");
    } finally {
      setLoading(false);
      fetchTransactions();
    }
  };

  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <div className="flex flex-col">
      <label className={`after:content-['*'] after:text-red-500`}>
        Transaction Date
      </label>
      <div
        className="example-custom-input border border-gray-300 h-[44px] w-[140px] md:w-[200px] lg:w-full p-2 rounded-md"
        onClick={onClick}
        ref={ref}
      >
        <h3>{value ? value : "Select date"}</h3>
      </div>
    </div>
  ));
  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: onSubmit,
    validateOnChange: isAggregator
      ? createTransactionSchemaTwo
      : createTransactionSchema,
  });

  return (
    <FormikProvider value={formik}>
      {/* <Formik
        initialValues={initialValues}
        validationSchema={
          isAggregator ? createTransactionSchemaTwo : createTransactionSchema
        }
        onSubmit={onSubmit}
      >
        {({
          handleChange,
          errors,
          values,
          handleSubmit,
          setFieldValue,
          setValues,
        }) => {
          return (
            <Form> */}
      <div className="flex flex-col md:flex-row lg:flex-row h-[570px] overflow-auto">
        <div className="flex flex-col w-[300px] md:w-[450px] lg:w-[571px] bg-white h-max">
          <div className="flex justify-between">
            <div className="h-[120px] p-[24px] flex flex-col gap-[16px]">
              <div className="flex justify-between ">
                <PiUsers style={{ width: 26, height: 26 }} />
              </div>
              <div className="flex flex-col gap-[4px]">
                <h1 className="capitalize font-bold">
                  {requestType === "edit"
                    ? "Edit Transaction"
                    : "create new Collections"}
                </h1>
                <p className="text-sm">Enter the details below</p>
              </div>
            </div>
            <div className={`flex justify-end p-[8px] pt-[24px] ${isEdit ? "block" : "sm:block hidden"}`}>
              <IoCloseOutline
                style={{
                  width: 26,
                  height: 26,
                  cursor: "pointer",
                }}
                onClick={() => closeModal()}
              />
            </div>
          </div>
          <div className="p-[24px] flex justify-center">
            <div className="flex flex-col items-center gap-[4.2px]">
              <div className="flex justify-between w-full">
                {!isAggregator ? (
                  <SearchableDropdown2
                    label={"Aggregator Name"}
                    options={aggregatorList}
                    className={"w-full"}
                    fieldRequired={true}
                    error={itemErrors.aggregatorId}
                    selectedOption={
                      newCollection.aggregatorId || newCollection.aggregatorName
                        ? {
                            label: newCollection.aggregatorName,
                            value: newCollection.aggregatorId,
                          }
                        : null
                    }
                    dropDownStyle={{
                      display: isAggregator ? "none" : "block",
                    }}
                    placeholder="Select  aggregator"
                    handleChange={(e) => {
                      updateCollectionField({
                        aggregatorName: e.label,
                        aggregatorId: e.value,
                      });
                    }}
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="flex justify-between w-full">
                <SearchableDropdown2
                  label={"Collector Name"}
                  options={collectorList}
                  fieldRequired={true}
                  selectedOption={
                    newCollection.collectorId || newCollection.collectorName
                      ? {
                          label: newCollection.collectorName,
                          value: newCollection.collectorId,
                        }
                      : null
                  }
                  className={`${isAggregator ? "w-full" : "w-full"}`}
                  error={itemErrors.collectorId}
                  placeholder="Select collector"
                  handleChange={(e) => {
                    updateCollectionField({
                      collectorName: e.label,
                      collectorId: e.value,
                    });
                  }}
                />
              </div>
              <div className="flex justify-between w-full">
                <InputText
                  label={"Quantity(KG)"}
                  placeholder={"Enter quantity"}
                  name="quantity"
                  className={"w-full"}
                  textStyle={"w-full"}
                  fieldRequired={true}
                  value={newCollection.quantity}
                  error={itemErrors.quantity}
                  handleChange={(e) => {
                    updateCollectionField({
                      quantity: formatNumberOnInput(e.target.value, {
                        allowDecimals: true,
                        maxDecimals: 2,
                      }),
                    });
                  }}
                />
              </div>
              {program === 1 ? (
                <div className="flex justify-between w-full">
                  <InputText
                    label={"Unit Price(NGN)"}
                    placeholder={"Enter unit price"}
                    name="price"
                    className={"w-full"}
                    textStyle={"w-full"}
                    fieldRequired={true}
                    value={newCollection.price}
                    error={itemErrors.price}
                    handleChange={(e) => {
                      updateCollectionField({
                        price: e.target.value,
                      });
                    }}
                  />
                </div>
              ) : (
                ""
              )}
              {program === 1 ? (
                <div className="flex justify-between w-full">
                  <InputText
                    label={"Amount(NGN)"}
                    value={
                      parseNumberInput(newCollection.quantity) &&
                      parseNumberInput(newCollection.price)
                        ? toMoney(
                            parseNumberInput(newCollection.quantity) *
                              parseNumberInput(newCollection.price)
                          )
                        : ""
                    }
                    name={"amount"}
                    className={"w-full"}
                    textStyle={"w-full"}
                    disabled={true}
                  />
                </div>
              ) : (
                ""
              )}
              {(program === 2 || program === 3) ? (
                <div className="flex justify-between w-full">
                  <InputText
                    label={"Amount(NGN)"}
                    value={newCollection.amount}
                    name={"amount"}
                    className={"w-full"}
                    textStyle={"w-full"}
                    fieldRequired={true}
                    error={itemErrors.amount}
                    handleChange={(e) => {
                      updateCollectionField({
                        amount: formatNumberOnInput(e.target.value),
                      });
                    }}
                  />
                </div>
              ) : (
                ""
              )}
              <div className="flex justify-between w-full">
                <SearchableDropdown2
                  label={"Source of plastic"}
                  options={plasticSourceList}
                  fieldRequired={true}
                  error={itemErrors.sourceOfPlasticId}
                  selectedOption={
                    newCollection.sourceOfPlasticId ||
                    newCollection.sourceOfPlasticName
                      ? {
                          label: newCollection.sourceOfPlasticName,
                          value: newCollection.sourceOfPlasticId,
                        }
                      : null
                  }
                  className={"w-full"}
                  placeholder="Select source of plastic"
                  handleChange={(e) => {
                    updateCollectionField({
                      sourceOfPlasticId: e.value,
                      sourceOfPlasticName: e.label,
                    });
                  }}
                />
              </div>
              <div className="flex justify-between w-full">
                <SearchableDropdown2
                  label={"Type of plastic"}
                  options={plasticTypeList}
                  fieldRequired={true}
                  error={itemErrors.typeOfPlasticId}
                  selectedOption={
                    newCollection.typeOfPlasticId ||
                    newCollection.typeOfPlasticName
                      ? {
                          label: newCollection.typeOfPlasticName,
                          value: newCollection.typeOfPlasticId,
                        }
                      : null
                  }
                  placeholder="Select type of plastics"
                  className={"w-full"}
                  handleChange={(e) => {
                    updateCollectionField({
                      typeOfPlasticId: e.value,
                      typeOfPlasticName: e.label,
                    });
                  }}
                />
              </div>

              <div className="flex flex-col w-full">
                <DatePicker
                  selected={
                    newCollection.transactionDate
                      ? moment(newCollection.transactionDate).toDate()
                      : startDate || null
                  }
                  onChange={(date) => {
                    setStartDate(date);
                    updateCollectionField({
                      transactionDate: moment(date).format("YYYY-MM-DD"),
                    });
                  }}
                  customInput={<ExampleCustomInput />}
                  showYearDropdown
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                />
                {itemErrors.transactionDate ? (
                  <p className="text-red-500">{itemErrors.transactionDate}</p>
                ) : (
                  ""
                )}
              </div>

              <br />
              {isEdit ? (
                <div className="flex gap-2 justify-center items-center w-full">
                  <button
                    type="button"
                    className="px-2 w-[120px] lg:w-[194px] h-[40px] bg-white font-normal text-xs flex justify-center items-center disabled:cursor-not-allowed border border-pfBlack md:w-272"
                    onClick={() => closeModal()}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={loading ? true : false}
                    onClick={handleUpdate}
                    className="bg-green-700 text-white flex justify-center items-center h-[40px] w-[135px] lg:w-[194px]"
                  >
                    {loading ? "Updating..." : "Update Collection"}
                  </button>
                </div>
              ) : (
                <div className={`flex justify-center items-center`}>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="bg-white text-black border border-gray-300 flex justify-center items-center h-[40px] w-[275px] lg:w-[475px] rounded-[8px]"
                  >
                    Add Item
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {!isEdit && (
        <div className="w-[300px] lg:w-[448px] bg-gray-200 h-max ">
          <div className="h-[160px] p-[24px] mb-[20px]">
            <div className="flex justify-end p-[8px] sm:hidden">
              <IoCloseOutline
                style={{
                  width: 26,
                  height: 26,
                  cursor: "pointer",
                }}
                onClick={() => closeModal()}
              />
            </div>
            <div className="mb-[20px]">
              <h2 className="font-semibold">Collection items</h2>
              <p className="text-sm">All items being collected</p>
            </div>
          </div>
          <div
            className={`pl-[24px] overflow-auto h-[430px] mb-[26px] mt-[50px] ${
              program === 2 ? "h-[430px]" : "h-[510px]"
            }
`}
          >
            <FieldArray name="plastics">
              {({ form, insert, remove, push }) => (
                <>
                  {form.values.plastics.map((data, index) => (
                    <div
                      className="bg-white mb-2 w-[396px] p-[12px] rounded-[8px] relative"
                      key={index}
                    >
                      {user.userType !== "AGGREGATOR" ? (
                        <div className="flex items-center gap-4">
                          <p>Aggregator name</p>{" "}
                          <p className="text-sm">{data?.aggregatorName}</p>
                        </div>
                      ) : (
                        ""
                      )}
                      <div className="flex items-center gap-4">
                        <p>Collectors name</p>{" "}
                        <p className="text-sm">{data?.collectorName}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <p>Quantity</p>{" "}
                        <p className="text-sm">{data?.quantity}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p>Amount</p> <p className="text-sm">{data?.amount}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p>Type of Plastic</p>{" "}
                        <p className="text-sm">{data?.typeOfPlasticName}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p>Source of Plastic</p>{" "}
                        <p className="text-sm">{data?.sourceOfPlasticName}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p>Transaction Date</p>{" "}
                        <p className="text-sm">{data?.transactionDate}</p>
                      </div>
                      <div className="flex h-[120px] absolute top-[10px] right-[12px]">
                        <IoCloseOutline
                          style={{
                            width: 26,
                            height: 26,
                            cursor: "pointer",
                            justifySelf: "flex-end",
                          }}
                          onClick={() => remove(index)}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </FieldArray>
          </div>
          <div className="flex gap-2 justify-center items-center mb-[20px] mt-[60px]">
            <button
              className="px-2 w-[120px] lg:w-[194px] h-[40px] bg-white font-normal text-xs flex justify-center items-center disabled:cursor-not-allowed border border-pfBlack md:w-272"
              onClick={() => closeModal()}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={loading ? true : false}
              onClick={() => {
                if (!formik.values.plastics.length) {
                  toast.error("Please add at least one item");
                  return;
                }
                formik.handleSubmit();
              }}
              className="bg-green-700 text-white flex justify-center items-center h-[40px] w-[135px] lg:w-[194px]"
            >
              {loading ? "Creating..." : "Create Collection"}
            </button>
          </div>
        </div>
        )}
      </div>
      {/* </Form>
          );
        }}
      </Formik> */}
    </FormikProvider>
  );
};

// <div className="pl-[24px] overflow-auto h-[445px] mb-[10px]">
//     <div className="bg-white mb-2 w-[396px] p-[12px] rounded-[8px] relative">
//       <div className="flex gap-4">
//         <p>Aggregator name</p> <p>{data?.aggregatorName}</p>
//       </div>
//       <div className="flex gap-4">
//         <p>Collectors name</p> <p>{data?.collectorName}</p>
//       </div>
//       <div className="flex gap-6">
//         <p>Quantity</p> <p>{data?.quantity}</p>
//       </div>
//       <div className="flex gap-4">
//         <p>Amount</p> <p>{data?.amount}</p>
//       </div>
//       <div className="flex gap-4">
//         <p>Type of Plastic</p> <p>{data?.typeOfPlasticName}</p>
//       </div>
//       <div className="flex gap-4">
//         <p>Source of Plastic</p> <p>{data?.sourceOfPlasticName}</p>
//       </div>
//       <div className="flex h-[120px] absolute top-[10px] right-[12px]">
//         <IoCloseOutline
//           style={{
//             width: 26,
//             height: 26,
//             cursor: "pointer",
//             justifySelf: "flex-end",
//           }}
//           onClick={() => remove(index)}
//         />
//       </div>
//     </div>
//   </div>
